import { ActionType, TenantRegion, UserId } from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader } from "components/layout";
import { CrmTable, CrmTableProps } from "components/table/crm-table";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import {
    UserManagementGetImpliedUsersQuery,
    UserManagementGetRolesQuery,
    UserManagementGetUsersQuery,
    useUserManagementGetImpliedUsersQuery,
    useUserManagementGetRolesQuery,
    useUserManagementGetUsersQuery,
} from "generated/graphql";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useRegionsWithPermission } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import { statusForUser } from "./helper";
import { ManageUsersNewAccount } from "./manage-users-new";
import { UserPermissionsCell } from "./user-permissions-cell";

interface DisplayableUser {
    id: UserId;
    email: string;
    name: string;
    status: string;
    placeholder: unknown;
    permissions: UserManagementGetUsersQuery["userManagementGetUsers"][0]["user"]["permissions"];
    source: "direct" | "implied";
}

const initialSort = [
    {
        id: "email",
        desc: false,
    },
];

const generateDisplayableUser = (
    user:
        | UserManagementGetUsersQuery["userManagementGetUsers"][0]
        | UserManagementGetImpliedUsersQuery["userManagementGetImpliedUsers"][0],
    source: "direct" | "implied",
): DisplayableUser => ({
    id: user.user.id,
    email: user.user.email,
    name: user.user.name,
    status: statusForUser(user.user),
    permissions: user.user.permissions,
    placeholder: null,
    source,
});

const generateAccountDisplayableUser = (
    user: UserManagementGetUsersQuery["userManagementGetUsers"][0],
): DisplayableUser => generateDisplayableUser(user, "direct");

const generateImpliedDisplayableUser = (
    user: UserManagementGetImpliedUsersQuery["userManagementGetImpliedUsers"][0],
): DisplayableUser => generateDisplayableUser(user, "implied");

const editColumn: CrmTableProps<DisplayableUser>["columns"][number] = {
    key: "placeholder",
    header: "",
    renderCell: ({ record }: { record: DisplayableUser }) =>
        record.source === "direct" ? (
            <Link to={`/settings/users/${record.id}`} className={defaultLinkStyle}>
                Edit
            </Link>
        ) : null,
};

const emailColumn: CrmTableProps<DisplayableUser>["columns"][number] = {
    key: "email",
    header: "Email",
    renderCell: ({ record }: { record: DisplayableUser }) =>
        record.source === "direct" ? (
            <Link to={`/settings/users/${record.id}`} className={defaultLinkStyle}>
                {record.email}
            </Link>
        ) : (
            <span>{record.email}</span>
        ),
};

const nameColumn: CrmTableProps<DisplayableUser>["columns"][number] = {
    key: "name",
    header: "Name",
};

const statusColumn: CrmTableProps<DisplayableUser>["columns"][number] = {
    key: "status",
    header: "Status",
};

const permissionsColumn = (
    regionsWithModifyAccess: TenantRegion[],
    roles: UserManagementGetRolesQuery,
): CrmTableProps<DisplayableUser>["columns"][number] => ({
    key: "permissions",
    header: "Permissions",
    enableSorting: false,
    renderCell: ({ record }: { record: DisplayableUser }) => {
        return (
            <UserPermissionsCell
                regions={regionsWithModifyAccess}
                permissions={record.permissions}
                roles={roles?.userManagementGetRoles}
            />
        );
    },
});

export const ManageUsersList: React.FC<unknown> = () => {
    const users = useUserManagementGetUsersQuery();
    const impliedUsers = useUserManagementGetImpliedUsersQuery();

    const roles = useUserManagementGetRolesQuery({}, stableQueryOptions());
    const regionsWithModifyAccess = useRegionsWithPermission(
        ActionType.SelfManageModifyUser,
    );

    const tableColumns: CrmTableProps<DisplayableUser>["columns"] = useMemo(
        () => [
            editColumn,
            emailColumn,
            nameColumn,
            statusColumn,
            permissionsColumn(regionsWithModifyAccess, roles.data),
        ],
        [roles.data, regionsWithModifyAccess],
    );

    return (
        <QueryRenderer name="ManageUsersList" query={users}>
            {data => {
                return (
                    <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-12 lg:gap-x-5">
                        <div className="lg:col-span-8 space-y-4">
                            <ContentFull>
                                <ContentHeader>Accounts</ContentHeader>
                                <CrmTable
                                    initialSort={initialSort}
                                    columns={tableColumns}
                                    data={data.userManagementGetUsers.map(
                                        generateAccountDisplayableUser,
                                    )}
                                />
                            </ContentFull>
                            {impliedUsers.data?.userManagementGetImpliedUsers?.length >
                            0 ? (
                                <ContentFull>
                                    <ContentHeader>Inherited Accounts</ContentHeader>
                                    <InlineBanner type="info">
                                        These accounts have access because they are
                                        members of a parent office
                                    </InlineBanner>
                                    <CrmTable
                                        initialSort={initialSort}
                                        columns={tableColumns}
                                        data={impliedUsers.data.userManagementGetImpliedUsers.map(
                                            generateImpliedDisplayableUser,
                                        )}
                                    />
                                </ContentFull>
                            ) : null}
                        </div>
                        <div className="lg:col-span-4">
                            <ContentFull>
                                <ContentHeader>Create new account</ContentHeader>
                                <ManageUsersNewAccount />
                            </ContentFull>
                        </div>
                    </div>
                );
            }}
        </QueryRenderer>
    );
};
